import { z } from "zod/v4";

import type { ClaimKindType } from "../../schemas/ai/ui-actions/audit-content-spec";

/** What a section of a page is: one list, read by the reading, the cards, clauses and steering. */
export const SECTION_TYPES = [
  "navigation",
  "announcementBar",
  "hero",
  "buyBox",
  "productList",
  "cart",
  "footer",
  "popup",
  "content",
] as const;

/** What a section can hold. A new kind follows the decisions log's process for adding one. */
export const FINDING_KINDS = [
  "reviews",
  "guarantee",
  "returns",
  "freeShipping",
  "offer",
  "scarcity",
  "price",
  "compareAtPrice",
  "productImages",
  "video",
  "faq",
  "securePayment",
  "sizeGuide",
  "ingredients",
  "installments",
] as const;

export const SectionTypeSchema = z.enum(SECTION_TYPES);
export const FindingKindSchema = z.enum(FINDING_KINDS);

export const FindingSchema = z.object({
  type: FindingKindSchema,
  /** The read's element ids it rests on, like `e79`. */
  elements: z.array(z.string()),
  quote: z.string(),
  /** Whether an element it rests on shows when the page first loads. */
  shown: z.boolean(),
});

export const SectionLabelSchema = z.object({
  /** The section's id in the read, `main`, or `popup:<n>`. */
  id: z.string(),
  type: SectionTypeSchema,
  findings: z.array(FindingSchema),
});

/**
 * One read's section reading: what each section is and what it holds, made by
 * web/ai and kept beside the read it describes. Whole: stored only when every
 * section was read.
 */
export const SectionReadingSchema = z.object({
  path: z.string(),
  /** The read it describes. */
  readAt: z.string(),
  /** The prompt, model and lists that made it. */
  version: z.number(),
  sections: z.array(SectionLabelSchema),
});

/** The claim a copy fact carries when a finding cites it; null where no sentence claims it. */
export const CLAIM_KIND_OF_FINDING: Record<
  FindingKindType,
  ClaimKindType | null
> = {
  reviews: "review",
  guarantee: "guarantee",
  returns: "guarantee",
  securePayment: "guarantee",
  freeShipping: "shipping",
  offer: "offer",
  scarcity: "scarcity",
  price: "price",
  compareAtPrice: "price",
  installments: "price",
  productImages: null,
  video: null,
  faq: null,
  sizeGuide: null,
  ingredients: null,
};

export type SectionTypeType = z.infer<typeof SectionTypeSchema>;
export type FindingKindType = z.infer<typeof FindingKindSchema>;
export type FindingType = z.infer<typeof FindingSchema>;
export type SectionLabelType = z.infer<typeof SectionLabelSchema>;
export type SectionReadingType = z.infer<typeof SectionReadingSchema>;
